import { Form, message } from "antd";
import { AgentForm } from "./components/AgentForm";
import {
  AgentFormValues,
  parseResponseFormatSchema,
} from "./components/agentFormValues";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createAgent, AgentInput } from "@/lib/api/resources/agents";

export default function AgentsCreate() {
  const [form] = Form.useForm<AgentFormValues>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (values: AgentInput) => createAgent(values),
    onSuccess: () => {
      message.success("Agent created successfully");
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      navigate("/agents");
    },
    onError: () => {
      message.error("Failed to create agent");
    },
  });

  const onFinish = (values: AgentFormValues) => {
    const payload: AgentInput = {
      name: values.name,
      aiProfile: values.aiProfile,
      instructions: values.instructions,
      allowedTools: values.allowedTools ?? [],
      maxTokensPerSession: values.maxTokensPerSession ?? null,
      overrideTemperature: values.overrideTemperature ?? null,
      overrideMaxTokens: values.overrideMaxTokens ?? null,
      overrideTopP: values.overrideTopP ?? null,
      responseFormatSchema: parseResponseFormatSchema(
        values.responseFormatSchema,
      ),
    };
    mutation.mutate(payload);
  };

  return (
    <div className="max-w-4xl mx-auto py-2 pb-12">
      <div className="mb-8 pl-1">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          Create New Agent
        </h1>
        <p className="text-gray-500 mt-2 text-sm max-w-2xl">
          Configure the core identities, LLM instructions, and specialized tool
          capabilities to shape the behavior of your new AI agent.
        </p>
      </div>
      <AgentForm
        form={form}
        onFinish={onFinish}
        isSubmitting={mutation.isPending}
      />
    </div>
  );
}
